/**
 * Import papers from a spreadsheet — for an agency moving to CivilHound from another system.
 * Upload → we match the columns → every row is checked → import the good ones (with prior attempts).
 */
import { useState } from 'react';
import { api, fileToBase64 } from '../shared/api.ts';
import { fmtDate, fmtDateTime } from '../shared/format.ts';
import { Link } from '../shared/router.tsx';
import { Card, Empty, ErrorBox, Field, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';

type Preview = {
  filename: string;
  headerRow: number;
  headers: string[];
  mapping: (string | null)[];
  fields: { key: string; label: string; required: boolean; multi: boolean; hint: string | null }[];
  missing: string[];
  counts: { rows: number; ready: number; skipped: number; attempts: number; warnings: number };
  rows: { row: number; ok: boolean; errors: string[]; warnings: string[]; caseNumber: string; paperType: string; defendant: string; address: string; courtDate: string | null; attempts: number }[];
};

export function ImportPage() {
  const meta = useLoad(() => api.get<any>('/api/meta'));
  const history = useLoad(() => api.get<any>('/api/import/batches'));
  const [file, setFile] = useState<{ filename: string; dataBase64: string } | null>(null);
  const [mapping, setMapping] = useState<(string | null)[] | null>(null);
  const [options, setOptions] = useState({ markReviewed: false, includeDuplicates: false, defaultCustomerId: '', defaultCourtId: '' });
  const [pv, setPv] = useState<Preview | null>(null);
  const [show, setShow] = useState<'all' | 'problems' | 'ready'>('all');
  const [result, setResult] = useState<any>(null);
  const a = useAction();

  const opts = (o = options) => ({ markReviewed: o.markReviewed, includeDuplicates: o.includeDuplicates, defaultCustomerId: o.defaultCustomerId || undefined, defaultCourtId: o.defaultCourtId || undefined });
  const preview = (f = file, m = mapping, o = options) =>
    f &&
    a.run(async () => {
      const r = await api.post<Preview>('/api/import/preview', { ...f, mapping: m ?? undefined, options: opts(o) });
      setPv(r);
      setMapping(r.mapping);
    });
  const choose = async (fl: File | undefined) => {
    setResult(null);
    setPv(null);
    setMapping(null);
    if (!fl) return setFile(null);
    const f = { filename: fl.name, dataBase64: await fileToBase64(fl) };
    setFile(f);
    preview(f, null);
  };
  const setOpt = (patch: Partial<typeof options>) => {
    const o = { ...options, ...patch };
    setOptions(o);
    preview(file, mapping, o);
  };
  const run = () =>
    file &&
    pv &&
    window.confirm(`Import ${pv.counts.ready} paper(s)${pv.counts.attempts ? ` and ${pv.counts.attempts} prior attempt(s)` : ''}? ${pv.counts.skipped ? `${pv.counts.skipped} row(s) with problems will be skipped.` : ''}`) &&
    a.run(async () => {
      const r = await api.post('/api/import/run', { ...file, mapping, options: opts() });
      setResult(r);
      setPv(null);
      setFile(null);
      history.reload();
    });

  const rows = (pv?.rows ?? []).filter((r) => (show === 'all' ? true : show === 'problems' ? !r.ok || r.warnings.length : r.ok));

  return (
    <div className="stack">
      <div>
        <h1>Import papers</h1>
        <p className="muted">Bring your open papers over from your old system in one go — with the attempts already made, so the M-A-E-W circles pick up where they left off. Nothing is saved until you press Import.</p>
      </div>

      {result && (
        <Notice tone="ok">
          Imported <strong>{result.created}</strong> paper(s){result.attempts ? <> with <strong>{result.attempts}</strong> prior attempt(s)</> : ''}.
          {result.reviewed ? ` ${result.reviewed} marked reviewed.` : ''}
          {result.notReviewed?.length ? ` Rows ${result.notReviewed.join(', ')} have no fee, so they’re waiting in intake.` : ''}
          {result.skipped?.length ? ` ${result.skipped.length} row(s) skipped — fix them in the spreadsheet and import just those rows.` : ''}{' '}
          <Link to="/papers">Open papers</Link>
        </Notice>
      )}

      <Card title="1 · Your spreadsheet">
        <div className="imp-step1">
          <Field label="Excel (.xlsx) or CSV file" hint="One row per paper. A title row above the column names is fine.">
            <input type="file" accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" aria-label="Spreadsheet file" onChange={(e) => choose(e.target.files?.[0])} />
          </Field>
          <div className="small">
            <a className="btn sm" href="/api/import/template">
              ⬇ Download a template
            </a>
            <div className="muted" style={{ marginTop: 6 }}>
              Column names don’t have to match — you can fix the matching below.
            </div>
          </div>
        </div>
        <div className="row gap wrap imp-opts">
          <label className="check">
            <input type="checkbox" checked={options.markReviewed} onChange={(e) => setOpt({ markReviewed: e.target.checked })} /> Mark imported papers as reviewed
          </label>
          <label className="check">
            <input type="checkbox" checked={options.includeDuplicates} onChange={(e) => setOpt({ includeDuplicates: e.target.checked })} /> Import possible duplicates anyway
          </label>
          <Field label="Customer when the sheet has none">
            <select value={options.defaultCustomerId} onChange={(e) => setOpt({ defaultCustomerId: e.target.value })} aria-label="Default customer">
              <option value="">— none —</option>
              {(meta.data?.customers ?? []).map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Court when the sheet has none">
            <select value={options.defaultCourtId} onChange={(e) => setOpt({ defaultCourtId: e.target.value })} aria-label="Default court">
              <option value="">— none —</option>
              {(meta.data?.courts ?? []).map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Card>

      <ErrorBox error={a.error} />
      {a.busy && !pv && <Spinner />}

      {pv && (
        <>
          <Card title={`2 · Match the columns (row ${pv.headerRow} has the names)`}>
            {pv.missing.length > 0 && <Notice tone="bad">Pick a column for: {pv.missing.join(', ')}.</Notice>}
            <div className="imp-map">
              {pv.headers.map((h, i) => (
                <label key={i} className="imp-map-row">
                  <span className="imp-col" title={h}>
                    {h || <em className="muted">(no name)</em>}
                  </span>
                  <span aria-hidden>→</span>
                  <select
                    aria-label={`Column ${h || i + 1}`}
                    value={mapping?.[i] ?? ''}
                    className={mapping?.[i] ? 'set' : ''}
                    onChange={(e) => {
                      const m = [...(mapping ?? pv.mapping)];
                      m[i] = e.target.value || null;
                      setMapping(m);
                      preview(file, m);
                    }}
                  >
                    <option value="">— don’t import —</option>
                    {pv.fields.map((f) => (
                      <option key={f.key} value={f.key} disabled={!f.multi && mapping?.includes(f.key) && mapping[i] !== f.key}>
                        {f.label}
                        {f.required ? ' *' : ''}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <p className="small muted">
              * required. Attempts: one column per attempt (“9/28/2026 10:15 AM no answer”), or several in one cell separated by “;”. Deputies are matched by badge number or name.
            </p>
          </Card>

          <Card title="3 · Check the rows">
            <div className="tiles">
              <div className="tile">
                <div className="tile-n">{pv.counts.ready}</div>
                <div className="tile-l">ready to import</div>
              </div>
              <div className="tile">
                <div className="tile-n" style={{ color: pv.counts.skipped ? 'var(--bad)' : undefined }}>
                  {pv.counts.skipped}
                </div>
                <div className="tile-l">will be skipped</div>
              </div>
              <div className="tile">
                <div className="tile-n">{pv.counts.attempts}</div>
                <div className="tile-l">prior attempts</div>
              </div>
              <div className="tile">
                <div className="tile-n">{pv.counts.warnings}</div>
                <div className="tile-l">with notes to check</div>
              </div>
            </div>
            <div className="tabs" role="tablist">
              {(['all', 'problems', 'ready'] as const).map((k) => (
                <button key={k} role="tab" aria-selected={show === k} className={show === k ? 'active' : ''} onClick={() => setShow(k)}>
                  {k === 'all' ? `All (${pv.counts.rows})` : k === 'problems' ? 'Problems' : 'Ready'}
                </button>
              ))}
            </div>
            <div className="table-wrap imp-table">
              <table className="table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th />
                    <th>Defendant</th>
                    <th>Paper</th>
                    <th>Address</th>
                    <th>Court date</th>
                    <th>Tries</th>
                    <th>Problems / notes</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.row} className={r.ok ? '' : 'imp-bad'}>
                      <td className="mono">{r.row}</td>
                      <td aria-label={r.ok ? 'Ready' : 'Skipped'}>{r.ok ? '✓' : '✕'}</td>
                      <td>
                        <strong>{r.defendant || '—'}</strong>
                        <div className="small mono muted">{r.caseNumber}</div>
                      </td>
                      <td className="small">{r.paperType || '—'}</td>
                      <td className="small">{r.address || '—'}</td>
                      <td className="small">{r.courtDate ? fmtDate(r.courtDate) : '—'}</td>
                      <td className="small">{r.attempts || ''}</td>
                      <td className="small">
                        {r.errors.map((e, i) => (
                          <div key={`e${i}`} className="imp-err">
                            {e}
                          </div>
                        ))}
                        {r.warnings.map((w, i) => (
                          <div key={`w${i}`} className="imp-warn">
                            {w}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="row gap wrap">
              <button className="btn primary" disabled={a.busy || pv.missing.length > 0 || pv.counts.ready === 0} onClick={run}>
                {a.busy ? 'Working…' : `Import ${pv.counts.ready} paper(s)`}
              </button>
              {pv.counts.skipped > 0 && <span className="small muted">Rows with problems are skipped. Fix them in the spreadsheet and import them later — rows already imported are caught as duplicates.</span>}
            </div>
          </Card>
        </>
      )}

      <Card title="Past imports">
        {!history.data ? (
          <Spinner />
        ) : history.data.batches.length === 0 ? (
          <Empty>No imports yet.</Empty>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>File</th>
                <th>Imported</th>
                <th>Skipped</th>
                <th>Attempts</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {history.data.batches.map((b: any) => (
                <tr key={b.id}>
                  <td className="small">{fmtDateTime(b.created_at)}</td>
                  <td>{b.filename}</td>
                  <td>{b.created}</td>
                  <td>{b.skipped}</td>
                  <td>{b.attempts}</td>
                  <td className="small">{b.by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
